import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { PlusIconSvg } from '@g99-product-web-ui/shared-util-lib';
import { Dropdown, DropdownChangeEvent } from 'primeng/dropdown';

import { ICustomDropdownOptionConfig } from '../../interfaces/dropdown-footer-config.interface';
import { DropdownValue } from '../../interfaces/dropdown.interface';
@Component({
  selector: 'lib-single-option-dropdown',
  templateUrl: './single-option-dropdown.component.html',
  styleUrls: ['./single-option-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleOptionDropdownComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SingleOptionDropdownComponent<T>
  implements ControlValueAccessor, OnChanges
{
  @ViewChild(Dropdown) public dropdown!: Dropdown;

  @Input() public placeholder = 'Select'; // Placeholder for input
  @Input() public label = ''; // Placeholder for input
  @Input() public hideLabelOnDesktop = false;
  @Input() public showClear = false;
  @Input() public inputId = ''; // ID for the input field
  @Input() public name = ''; // Name for the input field
  @Input() public isInvalid = false; // Error state
  @Input() public height = '40px'; // Error state
  @Input() public errorMessage: { [key: string]: string } = {}; // Error messages
  @Input() public formControls!: AbstractControl | null;

  // Accepting generic optionList and dynamic optionLabel, optionValue
  @Input() public optionList!: T[];
  @Input() public optionLabel!: keyof T; // key for label field
  @Input() public optionValue!: keyof T; // key for value field
  @Input() public dropdownHeight = '40px';
  @Input() public dropdownWidth = '100%';
  @Input() public itemTemplate: TemplateRef<{ $implicit: T }> | null = null;
  @Input() public selectedItemTemplate: TemplateRef<{ $implicit: T }> | null =
    null;

  @Input() public className = 'custom-dropdown-field';
  @Input() public showLabel = true;
  @Input() public border = '1px solid #eaecf0';
  @Input() public placeholderIcon = '';
  @Input() public marginTop = '12px';
  @Input() public isDropdownDisabled = false;
  @Input() public enableSmartFilter = false;
  @Input() public searchableFields: Array<keyof T> = [];
  @Input() public bufferSize = 100;
  @Input() public customOptionConfig: ICustomDropdownOptionConfig | null = null;
  @Input() public appendTo: HTMLElement | 'body' | null = null;
  @Input() public scrollHeight = '200px';
  @Input() public panelStyleClass = '';
  @Output() public customOptionAdd: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() public changeEvent = new EventEmitter<void>();

  public filteredList: T[] = [];
  public value: DropdownValue = null;
  public filterValue = '';
  public isAddingCustomOption = false;
  public customOptionValue = '';
  public customOptionError = '';
  protected addIcon = PlusIconSvg;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['optionList'] && Array.isArray(this.optionList)) {
      this.initializeBuffer();
    }
  }

  public initializeBuffer(): void {
    this.filteredList = this.optionList.slice(0, this.bufferSize);
  }

  public customFilterFunction(value: string): void {
    const term = (value || '').toLowerCase().trim();

    this.filteredList = this.optionList
      .filter((item) => {
        const safeItem = item as { [key: string]: unknown };

        const combined = (
          this.searchableFields.length > 0
            ? this.searchableFields
            : [this.optionLabel]
        )
          .map((field) => String(safeItem[field as string] ?? '').toLowerCase())
          .join(' ');

        return combined.includes(term);
      })
      .slice(0, this.bufferSize);
  }

  public resetFilter(options: T[]): void {
    this.filterValue = '';
    this.filteredList = options.slice(0, this.bufferSize);
  }

  public onChange: (value: DropdownValue) => void = (value: DropdownValue) => {
    // Invoke onChange callback if it has been registered
    if (this.onChange) {
      this.onChange(value);
    }
  };
  public onTouched: () => void = () => {
    // Invoke onTouched callback if it has been registered
    if (this.onTouched) {
      this.onTouched();
    }
  };

  // ControlValueAccessor implementation
  public writeValue(value: DropdownValue): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: DropdownValue) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  public onDropdownFocus(event: Event): void {
    const focusEvent = event as FocusEvent;
    const isUserInitiated = focusEvent.relatedTarget !== null;
    if (isUserInitiated) {
      this.onTouched(); // Mark as touched when the dropdown is focused (opened)
    }
  }
  public onInputChange(event: DropdownChangeEvent): void {
    this.value = event.value; // Directly access the value from the event
    this.onChange(this.value); // Notify value change
    this.changeEvent.emit();
    this.onTouched(); // Mark as touched
  }

  public getErrorMessage(): string | null {
    if (
      this.formControls &&
      this.formControls.invalid &&
      (this.formControls.dirty || this.formControls.touched)
    ) {
      for (const errorKey in this.formControls.errors) {
        if (this.errorMessage[errorKey]) {
          return this.errorMessage[errorKey];
        }
      }
    }
    return '';
  }

  // Add getters to cast the optionLabel and optionValue to string
  public getOptionLabel(): string {
    return (this.optionLabel as string) || 'name';
  }

  public getOptionValue(): string {
    return (this.optionValue as string) || 'id';
  }

  // Check if the optionList is an array of objects
  public isArrayOfObjects(): boolean {
    // Check if the first item in optionList is an object
    return (
      Array.isArray(this.optionList) &&
      this.optionList.length > 0 &&
      typeof this.optionList[0] === 'object'
    );
  }

  public handleCustomOptionClick(): void {
    this.customOptionAdd.emit();
  }
}
